import { useCallback, useState } from "react";
import { useClientConfig } from "@/api/queries";
import { SelectField } from "@/components/fields";
import type { ImportBankAccountsResponse } from "@/generated/backend/v1/bank_link";
import { useDraft } from "@/onboarding/useDraft";
import { CONN_FREE, ConnectPanel } from "@/onboarding/steps/Connect";
import { members, personLabel } from "@/plan-logic/questionnaire/draft";
import { errorText } from "@/components/ErrorPanel";
import { createFlinksSession, importFlinksAccounts, removeFlinksConnection } from "./api";
import { FlinksDialog } from "./FlinksDialog";
import { applyFlinksImport, type FlinksPatch, removeFromDraft } from "./toDraft";

/**
 * The Banking step's Flinks connections (like wealthica/OnboardingConnections.tsx for investments):
 * whose accounts, one institution per Flinks session, up to CONN_FREE. Each import goes straight into
 * the draft; the backend keeps the connection, reads it daily, and applies newer balances at check-ins.
 */
export function useFlinksConnect() {
  const { draft, form, patch } = useDraft();
  const available = !!useClientConfig().data?.flinksEnabled;
  const [ownerUuid, setOwnerUuid] = useState(draft.self.uuid);
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState<string>();
  const [message, setMessage] = useState<string>();
  const connections = draft.flinksConnections ?? [];

  const apply = useCallback(
    (changes: FlinksPatch) => {
      for (const [key, value] of Object.entries(changes)) {
        patch(key as keyof FlinksPatch, value as never);
      }
    },
    [patch],
  );

  const onImported = useCallback(
    (res: ImportBankAccountsResponse) => {
      const current = form.getValues();
      const owner = members(current).some((p) => p.uuid === ownerUuid)
        ? ownerUuid
        : current.self.uuid;
      apply(applyFlinksImport(current, res, owner));
      setOpen(false);
    },
    [form, ownerUuid, apply],
  );

  return {
    available,
    people: members(draft),
    ownerUuid,
    setOwnerUuid,
    connections,
    atLimit: connections.length >= CONN_FREE,
    open,
    connect: () => setOpen(true),
    close: () => setOpen(false),
    onImported,
    removing,
    message,
    /** Disconnects at the backend (and Flinks) first, then takes what it filled out of the draft. */
    remove: async (id: string) => {
      setRemoving(id);
      setMessage(undefined);
      try {
        await removeFlinksConnection(id);
        apply(removeFromDraft(form.getValues(), id));
      } catch (e) {
        setMessage(errorText(e));
      } finally {
        setRemoving(undefined);
      }
    },
  };
}

export function FlinksConnections({
  connection,
}: {
  connection: ReturnType<typeof useFlinksConnect>;
}) {
  const { people, connections } = connection;
  const label = (uuid: string) => {
    const i = people.findIndex((p) => p.uuid === uuid);
    return i < 0 ? "" : personLabel(people[i], i ? "Your partner" : "You");
  };
  // Shown once per institution and person (the panel keys its rows by name).
  const names = connections.map((c) =>
    people.length > 1 ? `${c.institution} (${label(c.ownerUuid)})` : c.institution,
  );
  const skipped = connections.flatMap((c) =>
    c.skipped.map((s) => ({ ...s, key: `${c.id}:${s.name}` })),
  );
  return (
    <>
      {connection.available && people.length > 1 && (
        <SelectField
          label="Whose accounts are you connecting?"
          value={connection.ownerUuid}
          options={people.map((p, i) => [p.uuid, personLabel(p, i ? "Your partner" : "You")])}
          onChange={connection.setOwnerUuid}
          testId="flinks-owner"
          hint="You can change the owner of each account when you check its balance."
        />
      )}
      <ConnectPanel
        via="Flinks"
        sub="One institution at a time. Connect one, then tell us whether there is another."
        connected={names}
        onAdd={connection.connect}
        onRemove={(i) => {
          if (!connection.removing) void connection.remove(connections[i].id);
        }}
        available={connection.available}
        atLimit={connection.atLimit}
        testId="bank-list-card"
      />
      {connection.message && (
        <p className="wp-form-err" role="alert">
          {connection.message}
        </p>
      )}
      {skipped.length > 0 && (
        <div className="note-soft" data-testid="flinks-skipped">
          We left these out. Add them yourself if they belong in your plan:
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {skipped.map((s) => (
              <li key={s.key}>
                {s.name} &mdash; {s.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      {connection.open && (
        <FlinksDialog
          title="Connect your bank"
          start={async () => (await createFlinksSession()).iframeUrl}
          finish={async (loginId) =>
            connection.onImported(await importFlinksAccounts(loginId, connection.ownerUuid))
          }
          onClose={connection.close}
        />
      )}
    </>
  );
}
